import { Component, OnInit, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../services/auth.service';
import { UserInfo } from '../../../models/auth.models';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';

@Component({
  selector: 'app-user-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ButtonModule, MenuModule, AvatarModule],
  template: `
    <div class="user-menu flex align-items-center gap-3" *ngIf="user">
      <div class="user-info text-right hidden md:block">
        <div class="font-semibold text-white">{{ user.FirstName || user.Username }}</div>
        <div class="text-sm text-gray-300">{{ user.Role }}</div>
      </div>
      <p-avatar
        [label]="getInitials()"
        styleClass="cursor-pointer"
        shape="circle"
        size="large"
        (click)="menu.toggle($event)"
      ></p-avatar>
      <p-menu #menu [model]="menuItems" [popup]="true"></p-menu>
    </div>
  `,
  styles: [`
    .user-menu {
      ::ng-deep .p-avatar {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        font-weight: 600;
        border: 2px solid rgba(255, 255, 255, 0.3);
        transition: all 0.3s ease;

        &:hover {
          transform: scale(1.05);
          border-color: rgba(255, 255, 255, 0.6);
        }
      }
    }
  `]
})
export class UserMenuComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  user: UserInfo | null = null;
  menuItems: MenuItem[] = [];

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.authService.authState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(state => {
      this.user = state.User;
      this.updateMenuItems();
    });
  }

  private updateMenuItems(): void {
    this.menuItems = [
      {
        label: 'Profile',
        icon: 'pi pi-user',
        command: () => this.goToProfile()
      },
      {
        label: 'Settings',
        icon: 'pi pi-cog',
        command: () => this.goToSettings()
      },
      {
        separator: true
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => this.logout()
      }
    ];
  }

  getInitials(): string {
    if (!this.user) return '?';

    if (this.user.FirstName && this.user.LastName) {
      return `${this.user.FirstName[0]}${this.user.LastName[0]}`.toUpperCase();
    }

    if (this.user.Username) {
      return this.user.Username.substring(0, 2).toUpperCase();
    }

    return '?';
  }

  goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  goToSettings(): void {
    this.router.navigate(['/settings']);
  }

  logout(): void {
    if (confirm('Are you sure you want to logout?')) {
      this.authService.logout();
    }
  }
}
